import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Menu,
  HelpCircle,
  Settings,
  LogOut,
  ChevronDown,
  Info,
  Globe,
  User,
} from "lucide-react";
import { BsChatText } from "react-icons/bs";
import styles from "../../styles/__dashboardLayout.module.scss";
import { IoNotifications } from "react-icons/io5";
import user from "../../assets/images/user.png";
import labImg from "../../assets/images/labUserImage.png";
import defaultAvatar from "../../assets/images/default_avatar.png";
import logo from "../../assets/images/logo.png";
import { useTranslation } from "react-i18next";
import { logout } from "../../reducer/logout/logoutReducer";
import { useDispatch, useSelector } from "react-redux";
import { CiSearch } from "react-icons/ci";
import { useSocketNotifications } from "../../hooks/useSocketNotifications";
import NotificationsPanel from "../notifications/NotificationsPanel";
import ChatDock from "../chat/ChatDock";
import useAdminChatUnread from "../../hooks/useAdminChatUnread";
import { getUnreadNotificationCount } from "../../reducer/notifications/notificationsReducer";
import { toast } from "../toast";

const LabDashboardLayout = ({
  children,
  currentPage,
  onNavigate,
  onLogout,
  image,
}) => {
  const { t, i18n } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] =
    useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Initialize socket notifications
  const { socket: notifSocket } = useSocketNotifications();
  const { unreadCount } = useSelector((state) => state.notifications);
  const location = useLocation();

  // Sidebar "Chat" badge = unread messages from the admin team in the support chat only.
  // Patient chats have their own badges (chat icons + ChatDock) and never count here.
  const onSupportChatPage = location.pathname === "/imaging_chat";
  const { count: chatUnreadCount } = useAdminChatUnread({
    socket: notifSocket,
    onChatPage: onSupportChatPage,
    onAdminMessage: () => {
      // Play notification sound
      try {
        const audio = new Audio(
          require("../../assets/notification/alert1.mp3"),
        );
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch (e) {}
      const isArabic = i18n.language === "ar";
      toast.info(
        isArabic
          ? "📩 وصلتك رسالة جديدة من المسؤول"
          : "📩 New chat received from admin",
        {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
        },
      );
    },
  });

  // Fetch avatar from profile API. Initialize from the cached URL so the header
  // avatar doesn't flash/reload on every page navigation (the layout remounts
  // per page). The profile page fires 'imagingAvatarUpdated' to force a refresh.
  const [headerAvatar, setHeaderAvatar] = useState(
    () => sessionStorage.getItem("provider_avatar_url") || null,
  );
  const [headerName, setHeaderName] = useState(
    () => sessionStorage.getItem("imaging_name") || "",
  );
  const [headerPhone, setHeaderPhone] = useState(
    () => sessionStorage.getItem("imaging_phone") || "",
  );

  useEffect(() => {
    const fetchAvatar = async () => {
      try {
        const token = sessionStorage.getItem("usr_1a2b3c");
        if (!token) return;
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v1/imaging/profile`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const data = await res.json();
        if (data?.data) {
          const p = data.data;
          const imgName =
            p.imaging_center_name_english ||
            p.imaging_center_name ||
            p.name ||
            "";
          if (imgName) {
            setHeaderName(imgName);
            sessionStorage.setItem("imaging_name", imgName);
          }
          const imgPhone =
            p.phone ||
            p.phone_number ||
            p.mobile_number ||
            p.contact_number ||
            p.mobile ||
            p.imaging_phone ||
            "";
          if (imgPhone) {
            setHeaderPhone(imgPhone);
            sessionStorage.setItem("imaging_phone", imgPhone);
          }
        }
        if (data?.data?.avatar_presigned_url) {
          setHeaderAvatar(data.data.avatar_presigned_url);
          sessionStorage.setItem(
            "provider_avatar_url",
            data.data.avatar_presigned_url,
          );
        } else {
          setHeaderAvatar(null);
          sessionStorage.removeItem("provider_avatar_url");
        }
      } catch (e) {
        /* silent */
      }
    };

    // Handle avatar updates from the profile page. Prefer the URL pushed in the
    // event detail (mirrors physio) so the header updates instantly without
    // depending on a fresh presigned URL from the backend; only fall back to a
    // re-fetch when no detail is provided.
    const onAvatarUpdated = (e) => {
      if (e?.detail?.removed || e?.detail?.avatarUrl === null) {
        setHeaderAvatar(null);
        sessionStorage.removeItem("provider_avatar_url");
        return;
      }
      if (e?.detail?.avatarUrl) {
        setHeaderAvatar(e.detail.avatarUrl);
        sessionStorage.setItem("provider_avatar_url", e.detail.avatarUrl);
        return;
      }
      fetchAvatar();
    };

    // Only fetch on mount if we don't already have a cached avatar/name/email.
    if (
      !sessionStorage.getItem("provider_avatar_url") ||
      !sessionStorage.getItem("imaging_name") ||
      !sessionStorage.getItem("imaging_phone")
    ) {
      fetchAvatar();
    }
    // Re-fetch / update when avatar is updated from profile page
    window.addEventListener("imagingAvatarUpdated", onAvatarUpdated);
    return () =>
      window.removeEventListener("imagingAvatarUpdated", onAvatarUpdated);
  }, []);

  // Fetch unread count on mount
  useEffect(() => {
    const token = sessionStorage.getItem("usr_1a2b3c");
    if (token) {
      dispatch(getUnreadNotificationCount());
    }
  }, [dispatch]);

  const languages = [
    { code: "en", name: "English", flag: "Flag_of_United_States-64x34.png" },
    { code: "ar", name: "العربية", flag: "Flag_of_Iraq-64x43.png" },
  ];

  // Get the current language name based on i18n.language
  const getCurrentLanguageName = () => {
    const currentLang = languages.find((lang) => lang.code === i18n.language);
    return currentLang ? currentLang.name : "English";
  };

  const [selectedLanguage, setSelectedLanguage] = useState(
    getCurrentLanguageName(),
  );

  // Sync selectedLanguage with i18n.language whenever it changes
  useEffect(() => {
    setSelectedLanguage(getCurrentLanguageName());
  }, [i18n.language]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language.name);
    i18n.changeLanguage(language.code);
    changeLanguage(language.code); // ✅ Apply language change
    setIsLanguageDropdownOpen(false);
  };

  const toggleNotificationDropdown = () => {
    setIsNotificationDropdownOpen(!isNotificationDropdownOpen);
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    document.dir = lang === "ar" ? "rtl" : "ltr";
  };

  const getNotificationIcon = (iconType) => {
    switch (iconType) {
      case "calendar-blue":
        return (
          <span className="calendar-blue-icon notification-info-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_2656_10702)">
                <mask
                  id="mask0_2656_10702"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="24"
                  height="24"
                >
                  <path d="M24 0H0V24H24V0Z" fill="white"></path>
                </mask>
                <g mask="url(#mask0_2656_10702)">
                  <path
                    d="M8 5.75C7.59 5.75 7.25 5.41 7.25 5V2C7.25 1.59 7.59 1.25 8 1.25C8.41 1.25 8.75 1.59 8.75 2V5C8.75 5.41 8.41 5.75 8 5.75Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M16 5.75C15.59 5.75 15.25 5.41 15.25 5V2C15.25 1.59 15.59 1.25 16 1.25C16.41 1.25 16.75 1.59 16.75 2V5C16.75 5.41 16.41 5.75 16 5.75Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M8.5 14.5009C8.37 14.5009 8.24 14.4709 8.12 14.4209C7.99 14.3709 7.89001 14.3009 7.79001 14.2109C7.61001 14.0209 7.5 13.7609 7.5 13.5009C7.5 13.2409 7.61001 12.9809 7.79001 12.7909C7.89001 12.7009 8 12.6309 8.12 12.5809C8.36 12.4809 8.64 12.4809 8.88 12.5809C9 12.6309 9.10999 12.7009 9.20999 12.7909C9.24999 12.8409 9.29999 12.8909 9.32999 12.9409C9.36999 13.0009 9.40001 13.0609 9.42001 13.1209C9.45001 13.1809 9.47001 13.2409 9.48001 13.3009C9.49001 13.3709 9.5 13.4409 9.5 13.5009C9.5 13.7609 9.38999 14.0209 9.20999 14.2109C9.10999 14.3009 9 14.3709 8.88 14.4209C8.76 14.4709 8.63 14.5009 8.5 14.5009Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M12 14.4992C11.87 14.4992 11.74 14.4692 11.62 14.4192C11.49 14.3692 11.39 14.2992 11.29 14.2092C11.11 14.0192 11 13.7592 11 13.4992C11 13.4392 11.01 13.3692 11.02 13.2992C11.03 13.2392 11.05 13.1792 11.08 13.1192C11.1 13.0592 11.13 12.9992 11.17 12.9392C11.21 12.8892 11.25 12.8392 11.29 12.7892C11.66 12.4192 12.33 12.4192 12.71 12.7892C12.75 12.8392 12.79 12.8892 12.83 12.9392C12.87 12.9992 12.9 13.0592 12.92 13.1192C12.95 13.1792 12.97 13.2392 12.98 13.2992C12.99 13.3692 13 13.4392 13 13.4992C13 13.7592 12.89 14.0192 12.71 14.2092C12.52 14.3892 12.27 14.4992 12 14.4992Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M8.5 18.0009C8.37 18.0009 8.24 17.9709 8.12 17.9209C8 17.8709 7.89001 17.8009 7.79001 17.7109C7.61001 17.5209 7.5 17.2609 7.5 17.0009C7.5 16.9409 7.50999 16.8709 7.51999 16.8109C7.52999 16.7409 7.54999 16.6809 7.57999 16.6209C7.59999 16.5609 7.63001 16.5009 7.67001 16.4409C7.70001 16.3909 7.75001 16.3409 7.79001 16.2909C7.89001 16.2009 8 16.1309 8.12 16.0809C8.36 15.9809 8.64 15.9809 8.88 16.0809C9 16.1309 9.10999 16.2009 9.20999 16.2909C9.24999 16.3409 9.29999 16.3909 9.32999 16.4409C9.36999 16.5009 9.40001 16.5609 9.42001 16.6209C9.45001 16.6809 9.47001 16.7409 9.48001 16.8109C9.49001 16.8709 9.5 16.9409 9.5 17.0009C9.5 17.2609 9.38999 17.5209 9.20999 17.7109C9.10999 17.8009 9 17.8709 8.88 17.9209C8.76 17.9709 8.63 18.0009 8.5 18.0009Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M20.5 9.83984H3.5C3.09 9.83984 2.75 9.49984 2.75 9.08984C2.75 8.67984 3.09 8.33984 3.5 8.33984H20.5C20.91 8.33984 21.25 8.67984 21.25 9.08984C21.25 9.49984 20.91 9.83984 20.5 9.83984Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M15.8205 22.7796C15.4405 22.7796 15.0805 22.6396 14.8205 22.3796C14.5105 22.0696 14.3705 21.6196 14.4405 21.1496L14.6305 19.7996C14.6805 19.4496 14.8905 19.0296 15.1405 18.7796L18.6804 15.2396C19.1604 14.7596 19.6305 14.5096 20.1405 14.4596C20.7705 14.3996 21.3804 14.6596 21.9604 15.2396C22.5704 15.8496 23.3904 17.0896 21.9604 18.5196L18.4205 22.0596C18.1705 22.3096 17.7504 22.5196 17.4004 22.5696L16.0504 22.7596C15.9704 22.7696 15.9005 22.7796 15.8205 22.7796ZM20.3104 15.9496C20.3004 15.9496 20.2904 15.9496 20.2804 15.9496C20.1404 15.9596 19.9504 16.0896 19.7404 16.2996L16.2005 19.8396C16.1705 19.8696 16.1204 19.9696 16.1204 20.0096L15.9405 21.2596L17.1905 21.0796C17.2305 21.0696 17.3304 21.0196 17.3604 20.9896L20.9004 17.4496C21.3404 17.0096 21.4004 16.7896 20.9004 16.2896C20.7404 16.1396 20.5104 15.9496 20.3104 15.9496Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M20.9206 19.2509C20.8506 19.2509 20.7806 19.2409 20.7206 19.2209C19.4006 18.8509 18.3506 17.8009 17.9806 16.4809C17.8706 16.0809 18.1006 15.6709 18.5006 15.5509C18.9006 15.4409 19.3106 15.6709 19.4306 16.0709C19.6606 16.8909 20.3106 17.5409 21.1306 17.7709C21.5306 17.8809 21.7606 18.3009 21.6506 18.7009C21.5506 19.0309 21.2506 19.2509 20.9206 19.2509Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M12 22.75H8C4.35 22.75 2.25 20.65 2.25 17V8.5C2.25 4.85 4.35 2.75 8 2.75H16C19.65 2.75 21.75 4.85 21.75 8.5V12C21.75 12.41 21.41 12.75 21 12.75C20.59 12.75 20.25 12.41 20.25 12V8.5C20.25 5.64 18.86 4.25 16 4.25H8C5.14 4.25 3.75 5.64 3.75 8.5V17C3.75 19.86 5.14 21.25 8 21.25H12C12.41 21.25 12.75 21.59 12.75 22C12.75 22.41 12.41 22.75 12 22.75Z"
                    fill="#2B7FFF"
                  ></path>
                </g>
              </g>
              <defs>
                <clipPath id="clip0_2656_10702">
                  <rect width="24" height="24" fill="white"></rect>
                </clipPath>
              </defs>
            </svg>
          </span>
        );
      case "calendar-red":
        return (
          <span className="calendar-red-icon notification-info-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_2656_10702)">
                <mask
                  id="mask0_2656_10702"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="24"
                  height="24"
                >
                  <path d="M24 0H0V24H24V0Z" fill="white"></path>
                </mask>
                <g mask="url(#mask0_2656_10702)">
                  <path
                    d="M8 5.75C7.59 5.75 7.25 5.41 7.25 5V2C7.25 1.59 7.59 1.25 8 1.25C8.41 1.25 8.75 1.59 8.75 2V5C8.75 5.41 8.41 5.75 8 5.75Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M16 5.75C15.59 5.75 15.25 5.41 15.25 5V2C15.25 1.59 15.59 1.25 16 1.25C16.41 1.25 16.75 1.59 16.75 2V5C16.75 5.41 16.41 5.75 16 5.75Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M8.5 14.5009C8.37 14.5009 8.24 14.4709 8.12 14.4209C7.99 14.3709 7.89001 14.3009 7.79001 14.2109C7.61001 14.0209 7.5 13.7609 7.5 13.5009C7.5 13.2409 7.61001 12.9809 7.79001 12.7909C7.89001 12.7009 8 12.6309 8.12 12.5809C8.36 12.4809 8.64 12.4809 8.88 12.5809C9 12.6309 9.10999 12.7009 9.20999 12.7909C9.24999 12.8409 9.29999 12.8909 9.32999 12.9409C9.36999 13.0009 9.40001 13.0609 9.42001 13.1209C9.45001 13.1809 9.47001 13.2409 9.48001 13.3009C9.49001 13.3709 9.5 13.4409 9.5 13.5009C9.5 13.7609 9.38999 14.0209 9.20999 14.2109C9.10999 14.3009 9 14.3709 8.88 14.4209C8.76 14.4709 8.63 14.5009 8.5 14.5009Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M12 14.4992C11.87 14.4992 11.74 14.4692 11.62 14.4192C11.49 14.3692 11.39 14.2992 11.29 14.2092C11.11 14.0192 11 13.7592 11 13.4992C11 13.4392 11.01 13.3692 11.02 13.2992C11.03 13.2392 11.05 13.1792 11.08 13.1192C11.1 13.0592 11.13 12.9992 11.17 12.9392C11.21 12.8892 11.25 12.8392 11.29 12.7892C11.66 12.4192 12.33 12.4192 12.71 12.7892C12.75 12.8392 12.79 12.8892 12.83 12.9392C12.87 12.9992 12.9 13.0592 12.92 13.1192C12.95 13.1792 12.97 13.2392 12.98 13.2992C12.99 13.3692 13 13.4392 13 13.4992C13 13.7592 12.89 14.0192 12.71 14.2092C12.52 14.3892 12.27 14.4992 12 14.4992Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M8.5 18.0009C8.37 18.0009 8.24 17.9709 8.12 17.9209C8 17.8709 7.89001 17.8009 7.79001 17.7109C7.61001 17.5209 7.5 17.2609 7.5 17.0009C7.5 16.9409 7.50999 16.8709 7.51999 16.8109C7.52999 16.7409 7.54999 16.6809 7.57999 16.6209C7.59999 16.5609 7.63001 16.5009 7.67001 16.4409C7.70001 16.3909 7.75001 16.3409 7.79001 16.2909C7.89001 16.2009 8 16.1309 8.12 16.0809C8.36 15.9809 8.64 15.9809 8.88 16.0809C9 16.1309 9.10999 16.2009 9.20999 16.2909C9.24999 16.3409 9.29999 16.3909 9.32999 16.4409C9.36999 16.5009 9.40001 16.5609 9.42001 16.6209C9.45001 16.6809 9.47001 16.7409 9.48001 16.8109C9.49001 16.8709 9.5 16.9409 9.5 17.0009C9.5 17.2609 9.38999 17.5209 9.20999 17.7109C9.10999 17.8009 9 17.8709 8.88 17.9209C8.76 17.9709 8.63 18.0009 8.5 18.0009Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M20.5 9.83984H3.5C3.09 9.83984 2.75 9.49984 2.75 9.08984C2.75 8.67984 3.09 8.33984 3.5 8.33984H20.5C20.91 8.33984 21.25 8.67984 21.25 9.08984C21.25 9.49984 20.91 9.83984 20.5 9.83984Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M15.8205 22.7796C15.4405 22.7796 15.0805 22.6396 14.8205 22.3796C14.5105 22.0696 14.3705 21.6196 14.4405 21.1496L14.6305 19.7996C14.6805 19.4496 14.8905 19.0296 15.1405 18.7796L18.6804 15.2396C19.1604 14.7596 19.6305 14.5096 20.1405 14.4596C20.7705 14.3996 21.3804 14.6596 21.9604 15.2396C22.5704 15.8496 23.3904 17.0896 21.9604 18.5196L18.4205 22.0596C18.1705 22.3096 17.7504 22.5196 17.4004 22.5696L16.0504 22.7596C15.9704 22.7696 15.9005 22.7796 15.8205 22.7796ZM20.3104 15.9496C20.3004 15.9496 20.2904 15.9496 20.2804 15.9496C20.1404 15.9596 19.9504 16.0896 19.7404 16.2996L16.2005 19.8396C16.1705 19.8696 16.1204 19.9696 16.1204 20.0096L15.9405 21.2596L17.1905 21.0796C17.2305 21.0696 17.3304 21.0196 17.3604 20.9896L20.9004 17.4496C21.3404 17.0096 21.4004 16.7896 20.9004 16.2896C20.7404 16.1396 20.5104 15.9496 20.3104 15.9496Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M20.9206 19.2509C20.8506 19.2509 20.7806 19.2409 20.7206 19.2209C19.4006 18.8509 18.3506 17.8009 17.9806 16.4809C17.8706 16.0809 18.1006 15.6709 18.5006 15.5509C18.9006 15.4409 19.3106 15.6709 19.4306 16.0709C19.6606 16.8909 20.3106 17.5409 21.1306 17.7709C21.5306 17.8809 21.7606 18.3009 21.6506 18.7009C21.5506 19.0309 21.2506 19.2509 20.9206 19.2509Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M12 22.75H8C4.35 22.75 2.25 20.65 2.25 17V8.5C2.25 4.85 4.35 2.75 8 2.75H16C19.65 2.75 21.75 4.85 21.75 8.5V12C21.75 12.41 21.41 12.75 21 12.75C20.59 12.75 20.25 12.41 20.25 12V8.5C20.25 5.64 18.86 4.25 16 4.25H8C5.14 4.25 3.75 5.64 3.75 8.5V17C3.75 19.86 5.14 21.25 8 21.25H12C12.41 21.25 12.75 21.59 12.75 22C12.75 22.41 12.41 22.75 12 22.75Z"
                    fill="#FF2056"
                  ></path>
                </g>
              </g>
              <defs>
                <clipPath id="clip0_2656_10702">
                  <rect width="24" height="24" fill="white"></rect>
                </clipPath>
              </defs>
            </svg>
          </span>
        );
      case "edit-yellow":
        return (
          <span className="edit-yellow-icon notification-info-icon">
            <svg
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.4528 1.0415H11.7503C11.9161 1.0415 12.0751 1.10735 12.1923 1.22456C12.3095 1.34177 12.3753 1.50074 12.3753 1.6665C12.3753 1.83226 12.3095 1.99124 12.1923 2.10845C12.0751 2.22566 11.9161 2.2915 11.7503 2.2915H10.5003C8.51866 2.2915 7.09533 2.29317 6.01199 2.43817C4.94616 2.5815 4.30366 2.85484 3.82866 3.329C3.35449 3.804 3.08199 4.44567 2.93866 5.5115C2.79366 6.59484 2.79199 8.01817 2.79199 9.99984C2.79199 11.9815 2.79366 13.4048 2.93866 14.4882C3.08199 15.554 3.35533 16.1965 3.82949 16.6715C4.30449 17.1457 4.94616 17.4182 6.01199 17.5615C7.09533 17.7065 8.51866 17.7082 10.5003 17.7082C12.482 17.7082 13.9053 17.7065 14.9887 17.5615C16.0545 17.4182 16.697 17.1448 17.172 16.6707C17.6462 16.1957 17.9187 15.554 18.062 14.4882C18.207 13.4048 18.2087 11.9815 18.2087 9.99984V8.74984C18.2087 8.58408 18.2745 8.42511 18.3917 8.3079C18.5089 8.19069 18.6679 8.12484 18.8337 8.12484C18.9994 8.12484 19.1584 8.19069 19.2756 8.3079C19.3928 8.42511 19.4587 8.58408 19.4587 8.74984V10.0473C19.4587 11.9715 19.4587 13.479 19.3003 14.6557C19.1387 15.859 18.8003 16.809 18.0553 17.5548C17.3095 18.3007 16.3595 18.6382 15.1553 18.7998C13.9795 18.9582 12.472 18.9582 10.5478 18.9582H10.4528C8.52866 18.9582 7.02116 18.9582 5.84449 18.7998C4.64116 18.6382 3.69116 18.2998 2.94533 17.5548C2.19949 16.809 1.86199 15.859 1.70033 14.6548C1.54199 13.479 1.54199 11.9715 1.54199 10.0473V9.95234C1.54199 8.02817 1.54199 6.52067 1.70033 5.344C1.86199 4.14067 2.20033 3.19067 2.94533 2.44484C3.69116 1.699 4.64116 1.3615 5.84533 1.19984C7.02116 1.0415 8.52866 1.0415 10.4528 1.0415ZM14.4753 1.8965C15.0253 1.36171 15.7636 1.06488 16.5307 1.07019C17.2978 1.07551 18.0319 1.38255 18.5744 1.92492C19.1169 2.46729 19.4241 3.20139 19.4296 3.96847C19.435 4.73555 19.1383 5.47395 18.6037 6.024L13.0637 11.5648C12.7545 11.8732 12.5603 12.0682 12.3445 12.2365C12.0898 12.4355 11.8139 12.606 11.522 12.7448C11.2745 12.8623 11.0137 12.949 10.5995 13.0873L8.17866 13.894C7.95964 13.9669 7.72464 13.9775 7.49997 13.9244C7.27531 13.8713 7.06985 13.7568 6.90662 13.5935C6.74339 13.4303 6.62882 13.2249 6.57576 13.0002C6.52269 12.7755 6.53322 12.5405 6.60616 12.3215L7.41283 9.90067C7.55116 9.48567 7.63783 9.22567 7.75533 8.97817C7.89533 8.68595 8.06477 8.41178 8.26366 8.15567C8.43199 7.939 8.62616 7.74567 8.93533 7.4365L14.4753 1.8965ZM17.7195 2.77984C17.4065 2.46698 16.982 2.29123 16.5395 2.29123C16.0969 2.29123 15.6725 2.46698 15.3595 2.77984L15.0462 3.094C15.0639 3.17456 15.0912 3.26845 15.1278 3.37567C15.247 3.71984 15.4737 4.17317 15.9003 4.59984C16.3158 5.01547 16.8362 5.31067 17.4062 5.454L17.7195 5.14067C18.0324 4.82766 18.2081 4.40322 18.2081 3.96067C18.2081 3.51812 18.0324 3.09284 17.7195 2.77984ZM16.4212 6.43984C15.8964 6.2136 15.4196 5.88949 15.0162 5.48484C14.6115 5.08138 14.2874 4.60456 14.0612 4.07984L9.84866 8.2915C9.50116 8.639 9.36533 8.7765 9.24866 8.92484C9.10699 9.10884 8.98494 9.30714 8.88449 9.5165C8.80283 9.68567 8.74116 9.869 8.58533 10.3348L8.22616 11.414L9.08616 12.2748L10.1653 11.9148C10.632 11.759 10.8145 11.6973 10.9845 11.6165C11.1945 11.5165 11.3917 11.3946 11.5762 11.2507C11.7237 11.1348 11.8612 10.999 12.2087 10.6523L16.4212 6.43984Z"
                fill="#FE9A00"
              />
            </svg>
          </span>
        );
      case "check-green":
        return (
          <span className="check-green-icon notification-info-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.0499 8.7498C13.1604 8.62603 13.2172 8.46343 13.2078 8.29778C13.1984 8.13212 13.1236 7.97697 12.9999 7.86646C12.8761 7.75596 12.7135 7.69914 12.5478 7.70852C12.3822 7.7179 12.227 7.7927 12.1165 7.91646L9.60736 10.7281L8.88236 9.91646C8.77156 9.79403 8.6168 9.72041 8.4519 9.71171C8.287 9.703 8.12535 9.75991 8.00227 9.87C7.87919 9.98009 7.80468 10.1344 7.79502 10.2993C7.78535 10.4641 7.84132 10.6261 7.95069 10.7498L9.14069 12.0831C9.19932 12.1488 9.27116 12.2014 9.35151 12.2373C9.43186 12.2733 9.5189 12.2919 9.60694 12.2919C9.69498 12.2919 9.78202 12.2733 9.86237 12.2373C9.94272 12.2014 10.0146 12.1488 10.0732 12.0831L13.0499 8.7498Z"
                fill="#00BC7D"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.5 1.0415C9.71917 1.0415 8.9725 1.29734 7.76917 1.709L7.16333 1.91734C5.92917 2.339 4.97583 2.66567 4.29417 2.94234C3.95083 3.0815 3.655 3.21734 3.4175 3.35484C3.18833 3.48817 2.95917 3.654 2.80333 3.8765C2.64917 4.09567 2.56917 4.36567 2.51917 4.6265C2.46667 4.8965 2.43583 5.22067 2.415 5.59317C2.375 6.3315 2.375 7.34484 2.375 8.65817V9.99234C2.375 15.0765 6.215 17.514 8.49833 18.5123L8.52083 18.5215C8.80417 18.6457 9.07083 18.7615 9.37667 18.8398C9.69917 18.9232 10.0425 18.9582 10.5 18.9582C10.9583 18.9582 11.3 18.9232 11.6233 18.8398C11.9292 18.7615 12.1958 18.6457 12.4783 18.5215L12.5017 18.5123C14.785 17.514 18.625 15.0765 18.625 9.99234V8.65817C18.625 7.34484 18.625 6.3315 18.585 5.59234C18.5712 5.2682 18.5364 4.9453 18.4808 4.62567C18.4308 4.36567 18.3508 4.09567 18.1975 3.87567C18.0408 3.654 17.8117 3.48817 17.5825 3.35484C17.2995 3.19859 17.0066 3.0608 16.7058 2.94234C16.0242 2.66567 15.0708 2.339 13.8367 1.91734L13.2308 1.709C12.0275 1.29734 11.2808 1.0415 10.5 1.0415ZM8.06667 2.92817C9.4125 2.46817 9.95167 2.2915 10.5 2.2915C11.0483 2.2915 11.5875 2.46817 12.9333 2.92817L13.41 3.0915C14.6708 3.52317 15.59 3.83817 16.235 4.09984C16.5575 4.2315 16.7908 4.3415 16.955 4.4365C17.035 4.48317 17.0928 4.52178 17.1283 4.55234C17.16 4.57734 17.1725 4.59234 17.1742 4.594C17.1851 4.61361 17.194 4.63427 17.2008 4.65567C17.2175 4.70123 17.235 4.77011 17.2533 4.86234C17.2994 5.12622 17.3273 5.39296 17.3367 5.66067C17.375 6.3615 17.375 7.33984 17.375 8.68067V9.99234C17.375 14.3057 14.15 16.4282 12.0008 17.3665C11.6917 17.5015 11.5125 17.5782 11.3133 17.629C11.1233 17.6782 10.8917 17.7082 10.5 17.7082C10.1083 17.7082 9.87667 17.6782 9.68667 17.629C9.48667 17.579 9.30833 17.5015 8.99917 17.3665C6.84917 16.4282 3.625 14.3048 3.625 9.99234V8.68067C3.625 7.33984 3.625 6.3615 3.66333 5.66067C3.67275 5.39296 3.70059 5.12622 3.74667 4.86234C3.76444 4.77011 3.78194 4.70123 3.79917 4.65567C3.81417 4.614 3.82417 4.59734 3.82583 4.594C3.83978 4.57872 3.85513 4.56477 3.87167 4.55234C3.90722 4.52178 3.965 4.48289 4.045 4.43567C4.20917 4.34067 4.4425 4.2315 4.765 4.10067C5.41 3.83817 6.32917 3.52317 7.59 3.09234L8.06667 2.92817Z"
                fill="#00BC7D"
              />
            </svg>
          </span>
        );
      default:
        return <Calendar size={20} className="dl-notification-icon-blue" />;
    }
  };

  const LayoutDashboard = () => (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.14258 4.5V9.5H11.3092V2H4.64258C3.26424 2 2.14258 3.12167 2.14258 4.5ZM9.64258 7.83333H3.80924V4.5C3.80924 4.04 4.18258 3.66667 4.64258 3.66667H9.64258V7.83333ZM12.9759 22H19.6426C21.0209 22 22.1426 20.8783 22.1426 19.5V14.5H12.9759V22ZM14.6426 16.1667H20.4759V19.5C20.4759 19.96 20.1026 20.3333 19.6426 20.3333H14.6426V16.1667ZM19.6426 2H12.9759V12.8333H22.1426V4.5C22.1426 3.12167 21.0209 2 19.6426 2ZM20.4759 11.1667H14.6426V3.66667H19.6426C20.1026 3.66667 20.4759 4.04 20.4759 4.5V11.1667ZM2.14258 19.5C2.14258 20.8783 3.26424 22 4.64258 22H11.3092V11.1667H2.14258V19.5ZM3.80924 12.8333H9.64258V20.3333H4.64258C4.18258 20.3333 3.80924 19.96 3.80924 19.5V12.8333Z"
        fill="#2D2D37"
      />
    </svg>
  );

  const Calendar = () => (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.14258 1.75C7.34149 1.75 7.53226 1.82902 7.67291 1.96967C7.81356 2.11032 7.89258 2.30109 7.89258 2.5V3.263C8.55458 3.25 9.28358 3.25 10.0866 3.25H14.1986C15.0016 3.25 15.7306 3.25 16.3926 3.263V2.5C16.3926 2.30109 16.4716 2.11032 16.6122 1.96967C16.7529 1.82902 16.9437 1.75 17.1426 1.75C17.3415 1.75 17.5323 1.82902 17.6729 1.96967C17.8136 2.11032 17.8926 2.30109 17.8926 2.5V3.327C18.1526 3.347 18.3989 3.37233 18.6316 3.403C19.8036 3.561 20.7526 3.893 21.5016 4.641C22.2496 5.39 22.5816 6.339 22.7396 7.511C22.7889 7.88567 22.8246 8.29567 22.8466 8.741C22.901 8.88855 22.9076 9.04947 22.8656 9.201C22.8926 10.002 22.8926 10.913 22.8926 11.944V14.056C22.8926 15.894 22.8926 17.35 22.7396 18.489C22.5816 19.661 22.2496 20.61 21.5016 21.359C20.7526 22.107 19.8036 22.439 18.6316 22.597C17.4916 22.75 16.0366 22.75 14.1986 22.75H10.0866C8.24858 22.75 6.79258 22.75 5.65358 22.597C4.48158 22.439 3.53258 22.107 2.78358 21.359C2.03558 20.61 1.70358 19.661 1.54558 18.489C1.39258 17.349 1.39258 15.894 1.39258 14.056V11.944C1.39258 10.913 1.39258 10.002 1.41958 9.2C1.37785 9.04836 1.38485 8.88744 1.43958 8.74C1.46091 8.29533 1.49624 7.88567 1.54558 7.511C1.70358 6.339 2.03558 5.39 2.78358 4.641C3.53258 3.893 4.48158 3.561 5.65358 3.403C5.88691 3.37233 6.13324 3.347 6.39258 3.327V2.5C6.39258 2.30109 6.4716 2.11032 6.61225 1.96967C6.7529 1.82902 6.94367 1.75 7.14258 1.75ZM2.90558 9.75C2.89258 10.403 2.89258 11.146 2.89258 12V14C2.89258 15.907 2.89458 17.262 3.03258 18.29C3.16758 19.295 3.42158 19.875 3.84458 20.298C4.26758 20.721 4.84758 20.975 5.85358 21.11C6.88158 21.248 8.23558 21.25 10.1426 21.25H14.1426C16.0496 21.25 17.4046 21.248 18.4326 21.11C19.4376 20.975 20.0176 20.721 20.4406 20.298C20.8636 19.875 21.1176 19.295 21.2526 18.289C21.3906 17.262 21.3926 15.907 21.3926 14V12C21.3926 11.146 21.3926 10.403 21.3796 9.75H2.90558ZM21.3106 8.25H2.97458C2.99058 8.06067 3.00991 7.88067 3.03258 7.71C3.16758 6.705 3.42158 6.125 3.84458 5.702C4.26758 5.279 4.84758 5.025 5.85358 4.89C6.88158 4.752 8.23558 4.75 10.1426 4.75H14.1426C16.0496 4.75 17.4046 4.752 18.4326 4.89C19.4376 5.025 20.0176 5.279 20.4406 5.702C20.8636 6.125 21.1176 6.705 21.2526 7.711C21.2752 7.881 21.2946 8.06067 21.3106 8.25ZM16.6426 15.75C16.4437 15.75 16.2529 15.829 16.1122 15.9697C15.9716 16.1103 15.8926 16.3011 15.8926 16.5C15.8926 16.6989 15.9716 16.8897 16.1122 17.0303C16.2529 17.171 16.4437 17.25 16.6426 17.25C16.8415 17.25 17.0323 17.171 17.1729 17.0303C17.3136 16.8897 17.3926 16.6989 17.3926 16.5C17.3926 16.3011 17.3136 16.1103 17.1729 15.9697C17.0323 15.829 16.8415 15.75 16.6426 15.75ZM14.3926 16.5C14.3926 16.2045 14.4508 15.9119 14.5638 15.639C14.6769 15.366 14.8427 15.1179 15.0516 14.909C15.2605 14.7001 15.5086 14.5343 15.7815 14.4213C16.0545 14.3082 16.3471 14.25 16.6426 14.25C16.9381 14.25 17.2306 14.3082 17.5036 14.4213C17.7766 14.5343 18.0246 14.7001 18.2336 14.909C18.4425 15.1179 18.6082 15.366 18.7213 15.639C18.8344 15.9119 18.8926 16.2045 18.8926 16.5C18.8926 17.0967 18.6555 17.669 18.2336 18.091C17.8116 18.5129 17.2393 18.75 16.6426 18.75C16.0458 18.75 15.4735 18.5129 15.0516 18.091C14.6296 17.669 14.3926 17.0967 14.3926 16.5Z"
        fill="#2D2D37"
      />
    </svg>
  );

  const Flask = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.00001 20.5C4.36217 20.5 3.90884 20.2154 3.64001 19.6462C3.37101 19.0769 3.43592 18.5474 3.83476 18.0577L9.50001 11.173V5H8.30776C8.09526 5 7.91709 4.92808 7.77326 4.78425C7.62959 4.64042 7.55776 4.46225 7.55776 4.24975C7.55776 4.03708 7.62959 3.859 7.77326 3.7155C7.91709 3.57183 8.09526 3.5 8.30776 3.5H15.6923C15.9048 3.5 16.0829 3.57192 16.2268 3.71575C16.3704 3.85958 16.4423 4.03775 16.4423 4.25025C16.4423 4.46292 16.3704 4.641 16.2268 4.7845C16.0829 4.92817 15.9048 5 15.6923 5H14.5V11.173L20.1653 18.0577C20.5641 18.5474 20.629 19.0769 20.36 19.6462C20.0912 20.2154 19.6378 20.5 19 20.5H5.00001ZM7.00001 18H17L13.6 14H10.4L7.00001 18ZM5.00001 19H19L13 11.7V5H11V11.7L5.00001 19Z"
        fill="#2D2D37"
      />
    </svg>
  );

  const logoutHandler = () => {
    const access_token = sessionStorage.getItem("usr_1a2b3c");
    const refresh_token = sessionStorage.getItem("usr_4d5e6f");
    dispatch(logout({ access_token, refresh_token }))
      .then((response) => {
        console.log("Logout response:", response);
        const status =
          response?.payload?.status_code ||
          response?.payload?.data?.status_code;
        if (status === 200) {
          sessionStorage.removeItem("usr_1a2b3c");
          sessionStorage.removeItem("usr_4d5e6f");
          sessionStorage.clear();
          console.log("Redirecting to login page...");
          navigate("/");
        } else {
          console.warn("Logout status not 200:", status);
          // Still clear session and redirect even if status is not 200
          sessionStorage.clear();
          navigate("/");
        }
      })
      .catch((error) => {
        console.log("error in logout: ", error);
        // Clear session and redirect even on error
        sessionStorage.clear();
        navigate("/");
      });
  };

  const CreditCard = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21.5 6.30775V17.6923C21.5 18.1974 21.325 18.625 20.975 18.975C20.625 19.325 20.1974 19.5 19.6923 19.5H4.30775C3.80258 19.5 3.375 19.325 3.025 18.975C2.675 18.625 2.5 18.1974 2.5 17.6923V6.30775C2.5 5.80258 2.675 5.375 3.025 5.025C3.375 4.675 3.80258 4.5 4.30775 4.5H19.6923C20.1974 4.5 20.625 4.675 20.975 5.025C21.325 5.375 21.5 5.80258 21.5 6.30775ZM4 8.404H20V6.30775C20 6.23075 19.9679 6.16025 19.9038 6.09625C19.8398 6.03208 19.7693 6 19.6923 6H4.30775C4.23075 6 4.16025 6.03208 4.09625 6.09625C4.03208 6.16025 4 6.23075 4 6.30775V8.404ZM4 11.596V17.6923C4 17.7692 4.03208 17.8398 4.09625 17.9038C4.16025 17.9679 4.23075 18 4.30775 18H19.6923C19.7693 18 19.8398 17.9679 19.9038 17.9038C19.9679 17.8398 20 17.7692 20 17.6923V11.596H4Z"
        fill="#2D2D37"
      />
    </svg>
  );

  const ReportIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.65038 11.7504V10.4504H15.3504V11.7504H8.65038ZM8.65038 8.15039V6.85039H15.3504V8.15039H8.65038ZM6.60038 14.6504H13.5004C13.8949 14.6504 14.2596 14.7336 14.5944 14.8999C14.9292 15.0661 15.2139 15.3008 15.4484 15.6041L17.4004 18.1544V4.50814C17.4004 4.41831 17.3716 4.34456 17.3139 4.28689C17.2562 4.22922 17.1825 4.20039 17.0926 4.20039H6.90813C6.8183 4.20039 6.74455 4.22922 6.68688 4.28689C6.62922 4.34456 6.60038 4.41831 6.60038 4.50814V14.6504ZM6.90813 19.8004H17.0176L14.4284 16.4029C14.3122 16.2499 14.1715 16.1362 14.0061 16.0619C13.8408 15.9876 13.6722 15.9504 13.5004 15.9504H6.60038V19.4926C6.60038 19.5825 6.62922 19.6562 6.68688 19.7139C6.74455 19.7716 6.8183 19.8004 6.90813 19.8004ZM17.0916 21.1004H6.90113C6.45063 21.1004 6.07122 20.945 5.76288 20.6341C5.45455 20.3233 5.30038 19.9428 5.30038 19.4926V4.50814C5.30038 4.05797 5.45588 3.67747 5.76688 3.36664C6.07788 3.05581 6.45863 2.90039 6.90913 2.90039H17.0996C17.5501 2.90039 17.9296 3.05581 18.2379 3.36664C18.5462 3.67747 18.7004 4.05797 18.7004 4.50814V19.4926C18.7004 19.9428 18.5449 20.3233 18.2339 20.6341C17.9229 20.945 17.5421 21.1004 17.0916 21.1004Z"
        fill="#2D2D37"
      />
    </svg>
  );

  const Toolkit = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.5 18.596V10.0385C2.5 9.53333 2.675 9.10575 3.025 8.75575C3.375 8.40575 3.80258 8.23075 4.30775 8.23075H7.25V6.30775C7.25 5.80258 7.425 5.375 7.775 5.025C8.125 4.675 8.55258 4.5 9.05775 4.5H14.9423C15.4474 4.5 15.875 4.675 16.225 5.025C16.575 5.375 16.75 5.80258 16.75 6.30775V8.23075H19.6923C20.1974 8.23075 20.625 8.40575 20.975 8.75575C21.325 9.10575 21.5 9.53333 21.5 10.0385V18.596C21.5 18.8522 21.4133 19.0668 21.24 19.24C21.0668 19.4133 20.8522 19.5 20.596 19.5H3.404C3.14783 19.5 2.93317 19.4133 2.76 19.24C2.58667 19.0668 2.5 18.8522 2.5 18.596ZM7.69225 14.6152V14.8652C7.69225 15.0778 7.62033 15.2559 7.4765 15.3997C7.33267 15.5434 7.1545 15.6152 6.942 15.6152C6.72933 15.6152 6.55125 15.5434 6.40775 15.3997C6.26408 15.2559 6.19225 15.0778 6.19225 14.8652V14.6152H4V18H20V14.6152H17.8077V14.8652C17.8077 15.0778 17.7358 15.2559 17.592 15.3997C17.4482 15.5434 17.2699 15.6152 17.0573 15.6152C16.8447 15.6152 16.6667 15.5434 16.523 15.3997C16.3795 15.2559 16.3077 15.0778 16.3077 14.8652V14.6152H7.69225ZM4 10.0385V13.1155H6.19225V12.8652C6.19225 12.6529 6.26417 12.4748 6.408 12.331C6.55183 12.1873 6.73008 12.1155 6.94275 12.1155C7.15525 12.1155 7.33333 12.1873 7.477 12.331C7.6205 12.4748 7.69225 12.6529 7.69225 12.8652V13.1155H16.3077V12.8652C16.3077 12.6529 16.3797 12.4748 16.5235 12.331C16.6673 12.1873 16.8455 12.1155 17.058 12.1155C17.2707 12.1155 17.4488 12.1873 17.5922 12.331C17.7359 12.4748 17.8077 12.6529 17.8077 12.8652V13.1155H20V10.0385C20 9.9615 19.9679 9.891 19.9038 9.827C19.8398 9.76283 19.7693 9.73075 19.6923 9.73075H4.30775C4.23075 9.73075 4.16025 9.76283 4.09625 9.827C4.03208 9.891 4 9.9615 4 10.0385ZM8.75 8.23075H15.25V6.30775C15.25 6.23075 15.2179 6.16025 15.1538 6.09625C15.0898 6.03208 15.0192 6 14.9423 6H9.05775C8.98075 6 8.91025 6.03208 8.84625 6.09625C8.78208 6.16025 8.75 6.23075 8.75 6.30775V8.23075Z"
        fill="#2D2D37"
      />
    </svg>
  );

  const navItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      path: "/imaging_dashboard",
    },
    {
      id: "lab_ordermanagement",
      label: "Order Management",
      icon: Flask,
      path: "/imaging_ordermanagement",
    },
    {
      id: "lab_report-management",
      label: "Report Management",
      icon: ReportIcon,
      path: "/imaging_report-management",
    },
    {
      id: "lab_technicianmanagement",
      label: "Technician Management",
      icon: Toolkit,
      path: "/imaging_technicianmanagement",
    },
    {
      id: "lab_earning_payment",
      label: "Earning and Payment",
      icon: CreditCard,
      path: "/imaging_earning_payment",
    },
    {
      id: "services-pricing",
      label: "Services and Pricing",
      icon: Settings,
      path: "/imaging_services",
    },
  ];

  return (
    <div className={styles["dl-dashboard"]}>
      {/* Header */}

      <header className={styles["dl-header"]}>
        <div className={styles["dl-header-left"]}>
          <button
            className={styles["dl-mobile-menu-btn"]}
            onClick={toggleSidebar}
          >
            <Menu size={24} />
          </button>
          <div className={styles["dl-logo"]}>
            <img src={logo} alt="TIRYAQ" />
          </div>
        </div>

        <div className={styles["dl-header-center"]}>
          {/* <div className={styles["dl-clinic-selector"]}>
            <span>{t("current_clinic")}</span>
            <select>
              <option>{t("baghdad_childrens_hospital")}</option>
              <option>{t("al_rashid_medical_center")}</option>
              <option>{t("ibn_sina_hospital")}</option>
            </select>
            <ChevronDown className={styles["dl-dropdown-icon"]} />
          </div> */}
          <div className={styles["dl-header-search"]}>
            <input
              type="text"
              placeholder="Search patient, appointment, test..."
            />
            <CiSearch />
          </div>
        </div>

        <div className={styles["dl-header-right"]}>
          {/* <button className={styles["dl-settings-btn"]}>
                    <Settings size={20} />
                </button> */}
          {/* <button className={styles["dl-notification-btn"]}>
                </button> */}
          <div className={styles["dl-notification-container"]}>
            <button
              className={styles["dl-notification-btn"]}
              onClick={toggleNotificationDropdown}
            >
              <IoNotifications size={20} />
              {unreadCount > 0 && (
                <span className={styles["dl-notification-badge"]}>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
            <NotificationsPanel
              isOpen={isNotificationDropdownOpen}
              onClose={() => setIsNotificationDropdownOpen(false)}
            />
          </div>

          {/* Language dropdown hidden (per request) */}
          {false && (
            <div className={styles["dl-language-selector"]}>
              <button
                className={styles["dl-language-btn"]}
                onClick={() =>
                  setIsLanguageDropdownOpen(!isLanguageDropdownOpen)
                }
              >
                <Globe size={16} />
                <span>{selectedLanguage}</span>
                <ChevronDown
                  size={16}
                  className={isLanguageDropdownOpen ? styles["rotated"] : ""}
                />
              </button>
              {isLanguageDropdownOpen && (
                <div className={styles["dl-language-dropdown"]}>
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`${styles["dl-language-option"]} ${
                        selectedLanguage === lang.name ? styles["active"] : ""
                      }`}
                      onClick={() => handleLanguageSelect(lang)}
                    >
                      <span className={styles["dl-language-name"]}>
                        {lang.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className={styles["dl-user-profile"]}>
            {/* <div className={styles["dl-rating"]}>
              <span>{t("user_rating")}</span>
              <div className={styles["dl-stars"]}>4★</div>
            </div> */}
            <div className={`dropdown ${styles["my-profile-dropdown"]}`}>
              <span
                className={`dropdown-toggle ${styles["dropdown-toggle"]}`}
                type="button"
                id="dropdownMenuButton"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <img
                  src={headerAvatar || image || defaultAvatar}
                  alt="User"
                  className={styles["dl-avatar"]}
                  onError={(e) => {
                    e.target.src = defaultAvatar;
                  }}
                />
                {headerName && (
                  <span
                    title={headerName}
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#1f1f1f",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "160px",
                      display: "inline-block",
                      verticalAlign: "middle",
                      marginInlineStart: "8px",
                      userSelect: "none",
                    }}
                  >
                    {headerName}
                  </span>
                )}
                <ChevronDown
                  size={18}
                  style={{
                    marginInlineStart: "4px",
                    color: "#1f1f1f",
                    flexShrink: 0,
                  }}
                />
              </span>
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby="dropdownMenuButton"
                style={{
                  minWidth: "230px",
                  padding: "6px 0",
                  borderRadius: "12px",
                  border: "1px solid #eef0f2",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  marginTop: "8px",
                }}
              >
                <li style={{ padding: "10px 16px 12px" }}>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#1f1f1f",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {headerName || t("profile_label")}
                  </div>
                  {headerPhone && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#8a8a8a",
                        marginTop: "2px",
                      }}
                    >
                      {headerPhone}
                    </div>
                  )}
                </li>
                <li>
                  <hr
                    className="dropdown-divider"
                    style={{ margin: "2px 0" }}
                  />
                </li>
                <li>
                  <Link
                    className={`dropdown-item ${styles["dropdown-item"]}`}
                    to={"/imaging_profile"}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 16px",
                      fontSize: "14px",
                    }}
                  >
                    <User size={16} /> {t("profile_label")}
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    className={`dropdown-item ${styles["dropdown-item"]}`}
                    onClick={onLogout || logoutHandler}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 16px",
                      fontSize: "14px",
                      width: "100%",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#dc2626",
                    }}
                  >
                    <LogOut size={16} /> {t("nav_logout")}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`${styles["dl-sidebar"]} ${
          isSidebarOpen ? styles["open"] : ""
        }`}
      >
        <nav className={styles["dl-nav"]}>
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.id}
                className={`${styles["dl-nav-item"]} ${
                  currentPage === item.id ? styles["active"] : ""
                }`}
                onClick={() => {
                  navigate(item.path); // go to the page
                  setIsSidebarOpen(false);
                }}
              >
                <IconComponent size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className={`${styles["dl-promo-banner"]} d-none`}>
          <div className={styles["dl-promo-header"]}>
            <div className={styles["dl-promo-icon"]}>
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M13.3333 8H18.6667C22.4373 8 24.324 8 25.4947 9.172C26.6667 10.3427 26.6667 12.2293 26.6667 16V17.408C26.6667 20.6147 26.6667 22.2187 25.8707 23.508C25.0733 24.796 23.6387 25.5133 20.7707 26.948C18.4293 28.1187 17.2587 28.704 16 28.704C14.7413 28.704 13.5707 28.1187 11.2293 26.948C8.36134 25.5133 6.92667 24.796 6.12934 23.508C5.33334 22.2187 5.33334 20.6133 5.33334 17.408V16C5.33334 12.2293 5.33334 10.3427 6.50534 9.172C7.676 8 9.56267 8 13.3333 8ZM16 13.3333C15.6213 13.3333 15.368 13.7867 14.8613 14.6973L14.7307 14.932C14.5867 15.1907 14.5147 15.3187 14.4027 15.404C14.2893 15.4893 14.1493 15.5213 13.8693 15.584L13.616 15.6427C12.632 15.8653 12.14 15.976 12.0227 16.352C11.9053 16.728 12.2413 17.1213 12.912 17.9053L13.0853 18.108C13.276 18.3307 13.372 18.4413 13.4147 18.58C13.4573 18.7187 13.4427 18.8667 13.4147 19.164L13.388 19.4347C13.2867 20.4813 13.236 21.0053 13.5413 21.2373C13.848 21.4693 14.3093 21.2573 15.2307 20.8333L15.468 20.724C15.7307 20.604 15.8613 20.544 16 20.544C16.1387 20.544 16.2693 20.604 16.532 20.724L16.7693 20.8333C17.6907 21.2587 18.152 21.4693 18.4587 21.2373C18.7653 21.0053 18.7133 20.4813 18.612 19.4347L18.5853 19.164C18.5573 18.8667 18.5427 18.7187 18.5853 18.58C18.628 18.4413 18.724 18.3307 18.9147 18.108L19.088 17.9053C19.7587 17.1213 20.0947 16.7293 19.9773 16.352C19.86 15.976 19.368 15.8653 18.384 15.6427L18.1307 15.584C17.8507 15.5213 17.7107 15.4907 17.5973 15.404C17.4853 15.3187 17.4133 15.1907 17.2693 14.932L17.1387 14.6973C16.632 13.7867 16.3787 13.3333 16 13.3333Z"
                  fill="#F54900"
                />
                <path
                  d="M14.6667 2.66663H17.3333C19.848 2.66663 21.104 2.66663 21.8853 3.44796C22.4373 3.99996 22.5987 4.78929 22.6467 6.09196C21.5533 5.99863 20.2587 5.99863 18.7987 5.99996H13.2C11.74 5.99996 10.4467 5.99996 9.352 6.09196C9.39866 4.78929 9.56133 3.99863 10.112 3.44796C10.896 2.66663 12.1547 2.66663 14.6667 2.66663Z"
                  fill="#F54900"
                />
              </svg>
            </div>
            <h3>{t("get_noticed_title")}</h3>
          </div>
          <div className={styles["dl-promo-content"]}>
            <p>{t("get_noticed_desc")}</p>
            <ul>
              <li>
                <span className={styles["dl-promo-content-icon"]}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.6667 7.99992C14.6667 11.6818 11.6819 14.6666 8 14.6666C4.3181 14.6666 1.33333 11.6818 1.33333 7.99992C1.33333 4.31802 4.3181 1.33325 8 1.33325C11.6819 1.33325 14.6667 4.31802 14.6667 7.99992ZM10.6869 5.9797C10.8821 6.17496 10.8821 6.49154 10.6869 6.6868L7.35355 10.0201C7.15829 10.2154 6.84171 10.2154 6.64644 10.0201L5.31311 8.68681C5.11785 8.49154 5.11785 8.17496 5.31311 7.9797C5.50837 7.78444 5.82496 7.78444 6.02022 7.9797L7 8.95948L8.48989 7.46959L9.97978 5.9797C10.175 5.78444 10.4916 5.78444 10.6869 5.9797Z"
                      fill="#F54900"
                    />
                  </svg>
                </span>
                {t("promo_higher_ranking")}
              </li>
              <li>
                <span className={styles["dl-promo-content-icon"]}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.6667 7.99992C14.6667 11.6818 11.6819 14.6666 8 14.6666C4.3181 14.6666 1.33333 11.6818 1.33333 7.99992C1.33333 4.31802 4.3181 1.33325 8 1.33325C11.6819 1.33325 14.6667 4.31802 14.6667 7.99992ZM10.6869 5.9797C10.8821 6.17496 10.8821 6.49154 10.6869 6.6868L7.35355 10.0201C7.15829 10.2154 6.84171 10.2154 6.64644 10.0201L5.31311 8.68681C5.11785 8.49154 5.11785 8.17496 5.31311 7.9797C5.50837 7.78444 5.82496 7.78444 6.02022 7.9797L7 8.95948L8.48989 7.46959L9.97978 5.9797C10.175 5.78444 10.4916 5.78444 10.6869 5.9797Z"
                      fill="#F54900"
                    />
                  </svg>
                </span>
                {t("promo_featured_section")}
              </li>
            </ul>
            <button className={styles["dl-promo-btn"]}>
              {t("promo_learn_more")} <Info />
            </button>
          </div>
        </div>

        <div className={styles["dl-sidebar-bottom"]}>
          <Link to={"/imaging_chat"}>
            <button
              className={`${styles["dl-nav-item"]} ${
                currentPage === "imaging_chat" ? styles["active"] : ""
              }`}
            >
              <BsChatText size={20} />
              <span>Chat</span>
              {chatUnreadCount > 0 && (
                <span
                  style={{
                    marginLeft: "auto",
                    background: "#ef4444",
                    color: "#fff",
                    fontSize: "10px",
                    fontWeight: "bold",
                    borderRadius: "50%",
                    minWidth: "18px",
                    height: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 4px",
                  }}
                >
                  {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
                </span>
              )}
            </button>
          </Link>
          {/* <Link to={"/help-and-support"}>
            <button
              className={`${styles["dl-nav-item"]} ${
                currentPage === "help-and-support" ? styles["active"] : ""
              }`}
            >
              <HelpCircle size={20} />
              <span>{t("nav_help_support")}</span>
            </button>
          </Link> */}
          <Link to={"/imaging_setting"}>
            <button
              className={`${styles["dl-nav-item"]} ${
                currentPage === "imaging_setting" ? styles["active"] : ""
              }`}
            >
              <Settings size={20} /> {t("nav_settings")}
            </button>
          </Link>
          {/* <button className={styles["dl-nav-item"]}>
            <Settings size={20} />
          </button> */}
          <Link to={"/"}>
            <button
              className={`${styles["dl-nav-item"]} ${
                currentPage === "logout" ? styles["active"] : ""
              }`}
              onClick={(e) => {
                e.preventDefault(); // prevent the default link navigation
                logoutHandler(); // call your logout function
              }}
            >
              <LogOut size={20} />
              <span>{t("nav_logout")}</span>
            </button>
          </Link>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className={styles["dl-sidebar-overlay"]}
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <main className={styles["dl-main-content"]}>
        {children}
      </main>

      {/* Minimized appointment-chat windows (bottom-right) */}
      <ChatDock />
    </div>
  );
};

export default LabDashboardLayout;
